import { CalendarClock, ChevronRight, Hexagon, Sparkles, Star, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { EmptyState, Loading, ProgressBar, SessionCard, ShareButton } from '../components';
import { formatRelative } from '../format';
import { scoreSessionCompatibility } from '../matching';
import { Link, useRouter } from '../router';
import { useApp } from '../store';
import type { MeSummary, SessionSummary } from '../types';

function GuestLanding() {
  return (
    <div className="landing">
      <section className="hero">
        <div>
          <span className="eyebrow">Compatibility-first LFG</span>
          <h1>Find teammates worth playing with again.</h1>
          <p>
            TeamCraft matches you with Destiny 2 and Helldivers 2 players who fit your schedule, platform,
            and vibe — then remembers who you clicked with, so a great squad never disappears after one run.
          </p>
          <div className="hero-actions">
            <Link to="/signup" className="primary">Create account <ChevronRight size={17} /></Link>
            <Link to="/sessions" className="secondary">Browse sessions</Link>
          </div>
        </div>
        <div className="hero-panel" aria-hidden="true">
          <div className="hex-orbit"><span /><span /><span /></div>
          <div className="hero-steps">
            <span><strong>1</strong> Pick a session that fits you</span>
            <span><strong>2</strong> Join in three taps</span>
            <span><strong>3</strong> Play, review, earn your squad</span>
          </div>
        </div>
      </section>
      <section className="landing-points">
        <div><Sparkles size={16} /><strong>Explainable matches</strong><p>Every recommendation shows why it fits: schedule, platform, voice, play style.</p></div>
        <div><Star size={16} /><strong>Reviews that matter</strong><p>One question after each session — would you play together again? No toxic star ratings.</p></div>
        <div><Hexagon size={16} /><strong>Earn it by playing</strong><p>Points come from confirmed sessions and new teammates, never from spam-creating groups.</p></div>
      </section>
    </div>
  );
}

function Dashboard() {
  const { profile } = useApp();
  const { navigate } = useRouter();
  const [summary, setSummary] = useState<MeSummary | null>(null);
  const [recommended, setRecommended] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([api.me(), api.sessions({ scope: 'upcoming' })])
      .then(([me, sessions]) => {
        if (!alive) return;
        setSummary(me);
        const mine = new Set(me.upcoming.map((session) => session.id));
        setRecommended(sessions.sessions.filter((session) => !mine.has(session.id)));
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading || !profile) return <Loading label="Loading your squad view…" />;

  const ranked = [...recommended]
    .map((session) => ({ session, fit: scoreSessionCompatibility(profile, session) }))
    .sort((a, b) => b.fit.score - a.fit.score);
  const best = ranked[0];

  return (
    <div className="dashboard">
      <header className="dash-header">
        <div>
          <span className="eyebrow">Welcome back</span>
          <h1>{profile.displayName}</h1>
        </div>
        <Link to="/profile" className="level-card" aria-label="View your profile and progression">
          <span className="level-hex lg">{profile.progression.level}</span>
          <span>
            <strong>{profile.progression.title}</strong>
            <span className="mono muted">{profile.progression.points} pts{profile.progression.nextLevelAt ? ` · ${profile.progression.nextLevelAt - profile.progression.points} to next` : ''}</span>
            <ProgressBar value={profile.progression.progress} />
          </span>
        </Link>
      </header>

      {summary && summary.pendingReviews.length > 0 && (
        <section className="pending-reviews" aria-label="Pending teammate reviews">
          <div className="section-heading">
            <div><span className="eyebrow">After game</span><h2>Confirm your sessions</h2></div>
          </div>
          {summary.pendingReviews.map((pending) => (
            <button key={pending.session.id} className="pending-card" onClick={() => navigate(`/sessions/${pending.session.id}`)}>
              <span>
                <strong>{pending.session.title}</strong>
                <small>{pending.teammates.length} teammate{pending.teammates.length === 1 ? '' : 's'} to review · closes {formatRelative(pending.closesAt)}</small>
              </span>
              <span className="pill mint">Review & earn points</span>
            </button>
          ))}
        </section>
      )}

      {summary && summary.upcoming.length > 0 && (
        <section aria-label="Your upcoming sessions">
          <div className="section-heading">
            <div><span className="eyebrow">Scheduled</span><h2>Your sessions</h2></div>
            <Link to="/sessions/new" className="secondary small">Host a session</Link>
          </div>
          <div className="session-grid">
            {summary.upcoming.map((session) => <SessionCard key={session.id} session={session} profile={profile} />)}
          </div>
        </section>
      )}

      <section aria-label="Recommended sessions">
        <div className="section-heading">
          <div><span className="eyebrow">Tonight</span><h2>Recommended for you</h2></div>
          <Link to="/sessions" className="ghost small">Browse all</Link>
        </div>
        {best ? (
          <div className="session-grid">
            {ranked.slice(0, 6).map(({ session }, index) => (
              <SessionCard key={session.id} session={session} profile={profile} featured={index === 0} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No open sessions right now"
            hint="Be the first — host a session and let compatible players find you."
            action={<Link to="/sessions/new" className="primary small">Host a session</Link>}
          />
        )}
      </section>

      <section className="dash-stats" aria-label="Your reputation">
        <div><Users size={15} /><strong>{profile.reputation.sessionsCompleted}</strong><span>Sessions played</span></div>
        <div><Star size={15} /><strong>{profile.reputation.playAgainRate === null ? '—' : `${profile.reputation.playAgainRate}%`}</strong><span>Would play again</span></div>
        <div><Sparkles size={15} /><strong>{profile.reputation.confirmedTeammates}</strong><span>New teammates met</span></div>
        <div><CalendarClock size={15} /><strong>{summary?.upcoming.length ?? 0}</strong><span>Upcoming</span></div>
      </section>

      <section className="invite-banner" aria-label="Invite friends">
        <div>
          <span className="eyebrow">Grow your squad</span>
          <strong>Better with friends</strong>
          <p>TeamCraft is more fun when your people are here. Send them an invite.</p>
        </div>
        <ShareButton url="/" title="TeamCraft" text="Find teammates worth playing with again — join me on TeamCraft" label="Invite friends" variant="primary" copyMessage="Invite link copied — send it to your friends." />
      </section>
    </div>
  );
}

export function Home() {
  const { authStatus } = useApp();
  if (authStatus === 'loading') return <Loading />;
  return authStatus === 'authed' ? <Dashboard /> : <GuestLanding />;
}
